import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { transLanguage } from '@op/utils';
import CardCommon from '@op/components/CardCommon';
import ChannelLabel from '@op/components/ChannelLabel';
import ContextComponent from '../../Summary/components/ContextComponent';
import useLanguage from '@op/hooks/useLanguage';
import { TContentKind, TLanguageType } from '../types';
import useEventDetailStats from '../hooks/useEventDetailStats';

interface IEventDetailPanelProps {
  /** 当前选中事件的 id（用作 stats_comments 的 event_id） */
  eventId: number | undefined;
  /** 请求参数（gameInfo + filter 等，含 start_time/end_time/task_id） */
  filterParams?: Record<string, any>;
  languageType: TLanguageType;
}

/** Top5 配置 */
const TOP_N = 5;

/** 右侧面板：Top5 统计 + 情感 Tab + 评论列表 — 自行获取数据 */
export default function EventDetailPanel({
  eventId,
  filterParams,
  languageType,
}: IEventDetailPanelProps) {
  const { formatMessage } = useLanguage();

  // 根据 eventId 自行获取 stats 数据
  const { statsData, contentsByKind, loading } = useEventDetailStats({
    eventId,
    filterParams,
  });

  const [activeId, setActiveId] = useState<TContentKind>('Positive');

  // 默认选中有数据的第一个 Tab
  useEffect(() => {
    if (!contentsByKind) return;
    const order: TContentKind[] = ['Positive', 'Neutral', 'Negative'];
    for (const kind of order) {
      if (contentsByKind[kind]?.mentions > 0) {
        setActiveId(kind);
        return;
      }
    }
  }, [contentsByKind]);

  // Top5 Language + Channel
  const renderRightContent = useMemo(() => {
    if (!statsData) return [];
    return [
      {
        title: 'Language',
        color: '#5086F3',
        list: [...(statsData.language_list || [])]
          .sort((a, b) => b.mentions - a.mentions)
          .slice(0, TOP_N),
      },
      {
        title: 'Channel',
        color: '#69D19C',
        list: [...(statsData.channel_list || [])]
          .sort((a, b) => b.mentions - a.mentions)
          .slice(0, TOP_N),
      },
    ];
  }, [statsData]);

  // 各情感数量
  const [positiveNum, negativeNum, neutralNum] = useMemo(() => {
    return (['Positive', 'Negative', 'Neutral'] as TContentKind[]).map(
      (key) => contentsByKind?.[key]?.mentions || 0,
    );
  }, [contentsByKind]);

  // 当前 Tab 评论列表
  const renderContentList = useMemo(() => {
    return contentsByKind?.[activeId]?.contents || [];
  }, [contentsByKind, activeId]);

  return (
    <CardCommon
      className="dbo-hotcard-flex-other"
      contentCard
      loading={loading}
      loadingHeight={500}
    >
      <>
        <div className="other-header">
          {renderRightContent.map(({ title, color, list }, index) => (
            <div
              key={title}
              className={index === 0 ? 'other-header-lf' : 'other-header-rt'}
            >
              <div className="other-header-item">
                <div className="title-text">
                  {formatMessage({ id: title })}
                </div>
                {list.map((t: any) => {
                  const isLanguage = title === 'Language';
                  return (
                    <div
                      key={t.language || t.channel}
                      className="title-flex"
                    >
                      <div className="title-flex-box">
                        {isLanguage ? (
                          <span className="title-flex-title">
                            {transLanguage(t.language)}
                          </span>
                        ) : (
                          <ChannelLabel channel_name={t.channel} />
                        )}
                      </div>
                      <div className="title-flex-text">
                        <span
                          className="text-line"
                          style={{
                            width: `${t.percentage}%`,
                            backgroundColor: color,
                          }}
                        />
                        <span className="text-num">
                          {t.mentions} ({t.percentage}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <Tabs
          className="dbo-hotcard-position"
          ceiling
          tabs={[
            {
              id: 'Positive',
              label: (
                <div className="tab-flex">
                  <SvgIcon type="laugh-green-color" className="tab-icon" />
                  {formatMessage({ id: 'Positive' })} ({positiveNum})
                </div>
              ),
            },
            {
              id: 'Neutral',
              label: (
                <div className="tab-flex">
                  <SvgIcon type="laugh-grey-color" className="tab-icon" />
                  {formatMessage({ id: 'Neutral' })} ({neutralNum})
                </div>
              ),
            },
            {
              id: 'Negative',
              label: (
                <div className="tab-flex">
                  <SvgIcon type="laugh-red-color" className="tab-icon" />
                  {formatMessage({ id: 'Negative' })} ({negativeNum})
                </div>
              ),
            },
          ]}
          animated={false}
          activeId={activeId}
          onActive={(tab) => setActiveId(tab.id as TContentKind)}
        />

        <div className="dbo-hotcard-context">
          <ContextComponent
            list={renderContentList}
            languageType={languageType}
            renderFunc={(value: string) => value}
          />
        </div>
      </>
    </CardCommon>
  );
}
